import { FC, useLayoutEffect, useRef, useState } from "react";
import cx from "classnames";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { REVIEWS_VALUES } from "./constants";
import useIsMobile from "@/custom_hooks/isMobile";

interface ReviewsSliderModernProps {
  className?: string;
}

const ReviewsSliderModern: FC<ReviewsSliderModernProps> = ({ className }) => {
  const isMobile = useIsMobile();
  const [currentIndex, setCurrentIndex] = useState(0);
  const reviewsCount = REVIEWS_VALUES.length;
  const isInfiniteMobile = reviewsCount > 1;
  const [mobileVirtualIndex, setMobileVirtualIndex] = useState(
    isInfiniteMobile ? 1 : 0,
  );
  const mobileScrollerRef = useRef<HTMLDivElement>(null);
  const mobileSlideRefs = useRef<Array<HTMLDivElement | null>>([]);
  const prevIsMobileRef = useRef<boolean | null>(null);
  const mobileGestureStartIndexRef = useRef(0);
  const mobileGestureActiveRef = useRef(false);
  const mobileScrollSettleTimeoutRef = useRef<number | null>(null);
  const mobileIsProgrammaticScrollRef = useRef(false);
  const [mobileSlideHeight, setMobileSlideHeight] = useState<number | null>(
    null,
  );

  const toRealIndex = (virtualIndex: number) => {
    if (!isInfiniteMobile) return 0;
    const normalized = ((virtualIndex - 1) % reviewsCount) + reviewsCount;
    return normalized % reviewsCount;
  };

  useLayoutEffect(() => {
    return () => {
      if (mobileScrollSettleTimeoutRef.current) {
        window.clearTimeout(mobileScrollSettleTimeoutRef.current);
      }
    };
  }, []);

  const handlePrev = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? REVIEWS_VALUES.length - 1 : prev - 1,
    );
  };

  const handleNext = () => {
    setCurrentIndex((prev) =>
      prev === REVIEWS_VALUES.length - 1 ? 0 : prev + 1,
    );
  };

  const currentReview = REVIEWS_VALUES[currentIndex];

  useLayoutEffect(() => {
    const prevIsMobile = prevIsMobileRef.current;
    prevIsMobileRef.current = isMobile;
    if (!isMobile || prevIsMobile === true) return;

    const nextVirtualIndex = isInfiniteMobile ? currentIndex + 1 : currentIndex;
    setMobileVirtualIndex(nextVirtualIndex);
    mobileGestureStartIndexRef.current = nextVirtualIndex;
    const scroller = mobileScrollerRef.current;
    if (!scroller) return;

    const width = scroller.clientWidth;
    if (width <= 0) return;

    const targetLeft = nextVirtualIndex * width;
    scroller.scrollTo({ left: targetLeft, behavior: "auto" });
  }, [currentIndex, isInfiniteMobile, isMobile]);

  useLayoutEffect(() => {
    if (!isMobile) return;

    const slide = mobileSlideRefs.current[mobileVirtualIndex];
    if (!slide) return;

    const sync = () => {
      setMobileSlideHeight(slide.getBoundingClientRect().height);
    };

    sync();

    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(sync);
      ro.observe(slide);
      return () => ro.disconnect();
    }

    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, [isMobile, mobileVirtualIndex]);

  if (isMobile) {
    const mobileItems = isInfiniteMobile
      ? [REVIEWS_VALUES[reviewsCount - 1], ...REVIEWS_VALUES, REVIEWS_VALUES[0]]
      : REVIEWS_VALUES;

    const handleMobileScroll = () => {
      const scroller = mobileScrollerRef.current;
      if (!scroller) return;

      const width = scroller.clientWidth;
      if (width <= 0) return;

      const lastVirtualIndex = isInfiniteMobile ? reviewsCount + 1 : reviewsCount - 1;
      const startVirtualIndex = mobileGestureStartIndexRef.current;
      const minLeft = Math.max(0, (startVirtualIndex - 1) * width);
      const maxLeft = Math.min(
        lastVirtualIndex * width,
        (startVirtualIndex + 1) * width,
      );

      let nextScrollLeft = scroller.scrollLeft;
      if (mobileGestureActiveRef.current && !mobileIsProgrammaticScrollRef.current) {
        if (nextScrollLeft < minLeft) nextScrollLeft = minLeft;
        if (nextScrollLeft > maxLeft) nextScrollLeft = maxLeft;
        if (nextScrollLeft !== scroller.scrollLeft) scroller.scrollLeft = nextScrollLeft;
      }

      const nextVirtualIndex = Math.round(nextScrollLeft / width);
      const clampedVirtualIndex = Math.max(0, Math.min(lastVirtualIndex, nextVirtualIndex));

      if (clampedVirtualIndex !== mobileVirtualIndex) {
        setMobileVirtualIndex(clampedVirtualIndex);
        setCurrentIndex(
          isInfiniteMobile ? toRealIndex(clampedVirtualIndex) : clampedVirtualIndex,
        );
      }

      if (mobileScrollSettleTimeoutRef.current) {
        window.clearTimeout(mobileScrollSettleTimeoutRef.current);
      }

      mobileScrollSettleTimeoutRef.current = window.setTimeout(() => {
        if (mobileIsProgrammaticScrollRef.current) return;

        const scrollerNow = mobileScrollerRef.current;
        if (!scrollerNow) return;

        const widthNow = scrollerNow.clientWidth;
        if (widthNow <= 0) return;

        const lastVirtualIndexNow = isInfiniteMobile ? reviewsCount + 1 : reviewsCount - 1;
        const rawVirtualIndex = Math.round(scrollerNow.scrollLeft / widthNow);
        const boundedRawVirtualIndex = Math.max(
          0,
          Math.min(lastVirtualIndexNow, rawVirtualIndex),
        );

        const startVirtualIndexNow = mobileGestureStartIndexRef.current;
        const delta = boundedRawVirtualIndex - startVirtualIndexNow;
        let limitedVirtualIndex =
          Math.abs(delta) <= 1
            ? boundedRawVirtualIndex
            : Math.max(
                0,
                Math.min(
                  lastVirtualIndexNow,
                  startVirtualIndexNow + Math.sign(delta),
                ),
              );

        const targetLeft = limitedVirtualIndex * widthNow;
        if (Math.abs(scrollerNow.scrollLeft - targetLeft) > 1) {
          scrollerNow.scrollTo({ left: targetLeft, behavior: "smooth" });
        }

        setMobileVirtualIndex(limitedVirtualIndex);
        setCurrentIndex(
          isInfiniteMobile ? toRealIndex(limitedVirtualIndex) : limitedVirtualIndex,
        );
        mobileGestureStartIndexRef.current = limitedVirtualIndex;
        mobileGestureActiveRef.current = false;

        if (isInfiniteMobile) {
          const jumpVirtualIndex =
            limitedVirtualIndex === 0
              ? reviewsCount
              : limitedVirtualIndex === reviewsCount + 1
                ? 1
                : null;

          if (jumpVirtualIndex !== null) {
            limitedVirtualIndex = jumpVirtualIndex;
            mobileIsProgrammaticScrollRef.current = true;
            scrollerNow.scrollTo({
              left: jumpVirtualIndex * widthNow,
              behavior: "auto",
            });
            setMobileVirtualIndex(jumpVirtualIndex);
            setCurrentIndex(toRealIndex(jumpVirtualIndex));
            mobileGestureStartIndexRef.current = jumpVirtualIndex;
            window.setTimeout(() => {
              mobileIsProgrammaticScrollRef.current = false;
            }, 0);
          }
        }
      }, 120);
    };

    const scrollToIndex = (index: number) => {
      const scroller = mobileScrollerRef.current;
      if (!scroller) return;

      mobileIsProgrammaticScrollRef.current = true;
      mobileGestureActiveRef.current = false;
      const targetVirtualIndex = isInfiniteMobile ? index + 1 : index;
      scroller.scrollTo({
        left: targetVirtualIndex * scroller.clientWidth,
        behavior: "smooth",
      });
      setMobileVirtualIndex(targetVirtualIndex);
      setCurrentIndex(index);
      mobileGestureStartIndexRef.current = targetVirtualIndex;

      window.setTimeout(() => {
        mobileIsProgrammaticScrollRef.current = false;
      }, 350);
    };

    return (
      <section className={cx(className, "relative mx-auto bg-white pt-15")}>
        <div className="mx-auto max-w-[1220px] px-4">
          <div
            className="overflow-hidden transition-[height] duration-300 ease-out"
            style={mobileSlideHeight ? { height: mobileSlideHeight } : undefined}
          >
            <div
              ref={mobileScrollerRef}
              onScroll={handleMobileScroll}
              onTouchStart={() => {
                mobileIsProgrammaticScrollRef.current = false;
                mobileGestureActiveRef.current = true;
                mobileGestureStartIndexRef.current = mobileVirtualIndex;
              }}
              onPointerDown={() => {
                mobileIsProgrammaticScrollRef.current = false;
                mobileGestureActiveRef.current = true;
                mobileGestureStartIndexRef.current = mobileVirtualIndex;
              }}
              className="scrollbar-hide flex h-full snap-x snap-mandatory items-start overflow-x-auto overflow-y-hidden scroll-smooth"
            >
              {mobileItems.map((review, index) => (
                <div
                  key={`${review.id}-${index}`}
                  className="w-full shrink-0 snap-start"
                  style={{ scrollSnapStop: "always" }}
                  ref={(node) => {
                    mobileSlideRefs.current[index] = node;
                  }}
                >
                  <div className="flex-1 text-center">
                    <div className="mx-auto mb-10 flex max-w-[810px] flex-col items-center justify-start">
                      <p className="text-dark text-center text-[24px] leading-[128%] font-light">
                        {`„${review.comment}“`}
                      </p>
                    </div>

                    <div className="flex items-center justify-center gap-3">
                      <div className="flex flex-col items-center justify-center gap-2">
                        {review.image ? (
                          <img
                            src={review.image}
                            alt={review.author}
                            className="h-14 w-14 shrink-0 rounded-lg bg-gray-200 object-cover ring-1 ring-gray-200"
                          />
                        ) : (
                          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-blue-500">
                            <span className="text-lg font-semibold text-white">
                              {review.author.charAt(0)}
                            </span>
                          </div>
                        )}

                        <div className="text-center">
                          <p className="text-dark text-2xl font-light">
                            {review.author}
                          </p>
                          <p className="text-grey-400 text-base font-light">
                            {review.authorTitle}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-15 flex justify-center gap-1">
            <div className="rounded-pill flex w-auto items-center gap-2 bg-[#F4F4F4] px-[24px] py-2.5">
              {REVIEWS_VALUES.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => scrollToIndex(index)}
                  className={cx(
                    "h-3.5 w-3.5 rounded-full transition-all",
                    index === currentIndex
                      ? "bg-[#212121]"
                      : "bg-[#e5e5e5] hover:bg-[#212121]",
                  )}
                  aria-label={`Go to review ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className={cx(className, "relative mx-auto bg-white pt-15 lg:pt-[100px]")}
    >
      <div className="mx-auto max-w-[1220px] px-4 md:px-0">
        <div className="relative flex items-center justify-center gap-[10px]">
          <button
            onClick={handlePrev}
            className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 transition-all hover:bg-gray-50 md:flex"
            aria-label="Previous review"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>

          <div className="flex-1 text-center">
            <div className="mx-auto mb-10 flex max-w-[810px] flex-col items-center justify-start">
              <p className="text-dark text-center text-[24px] leading-[128%] font-light md:text-[34px]">
                {`„${currentReview.comment}“`}
              </p>
            </div>

            <div className="flex items-center justify-center gap-3">
              <div className="flex flex-col items-center justify-center gap-2">
                {currentReview.image ? (
                  <img
                    src={currentReview.image}
                    alt={currentReview.author}
                    className="h-14 w-14 shrink-0 rounded-lg bg-gray-200 object-cover ring-1 ring-gray-200"
                  />
                ) : (
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-blue-500">
                    <span className="text-lg font-semibold text-white">
                      {currentReview.author.charAt(0)}
                    </span>
                  </div>
                )}

                <div className="text-center">
                  <div className="flex items-center justify-center gap-2">
                    <p className="text-dark text-2xl font-light">
                      {currentReview.author}
                    </p>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M5.5 8L7 9.5L10.5 6M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z"
                        stroke="#2196F3"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <p className="text-grey-400 text-base font-light">
                    {currentReview.authorTitle}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={handleNext}
            className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 transition-all hover:bg-gray-50 md:flex"
            aria-label="Next review"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </div>

        <div className="mt-15 flex justify-center gap-1">
          <div className="rounded-pill flex w-auto items-center gap-2 bg-[#F4F4F4] px-[24px] py-2.5">
            {REVIEWS_VALUES.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={cx(
                  "h-3.5 w-3.5 rounded-full transition-all",
                  index === currentIndex
                    ? "bg-[#212121]"
                    : "bg-[#e5e5e5] hover:bg-[#212121]",
                )}
                aria-label={`Go to review ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSliderModern;
